import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SkillCard = ({ skill, onHover, isActive }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getProficiencyColor = (level) => {
    switch (level) {
      case 'Expert': return 'text-success';
      case 'Advanced': return 'text-trust-builder';
      case 'Intermediate': return 'text-warning';
      case 'Learning': return 'text-conversion-accent';
      default: return 'text-muted-foreground';
    }
  };

  const getProficiencyWidth = (level) => {
    switch (level) {
      case 'Expert': return 'w-full';
      case 'Advanced': return 'w-4/5';
      case 'Intermediate': return 'w-3/5';
      case 'Learning': return 'w-2/5';
      default: return 'w-1/5';
    }
  };

  return (
    <div
      className={`relative group bg-card border border-border rounded-xl p-6 transition-smooth hover:border-conversion-accent/30 hover:shadow-elevation cursor-pointer ${
        isActive ? 'border-conversion-accent/50 shadow-subtle' : ''
      }`}
      onMouseEnter={() => {
        setIsHovered(true);
        onHover(skill);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        onHover(null);
      }}
    >
      {/* Skill Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-smooth ${
            isHovered ? 'bg-conversion-accent/20' : 'bg-muted/30'
          }`}>
            <Icon 
              name={skill?.icon} 
              size={24} 
              className={isHovered ? 'text-conversion-accent' : 'text-muted-foreground'} 
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{skill?.name}</h3>
            <p className="text-sm text-muted-foreground">{skill?.category}</p>
          </div>
        </div>
        
        <div className="text-right">
          <div className={`text-sm font-medium ${getProficiencyColor(skill?.proficiency)}`}>
            {skill?.proficiency}
          </div>
          <div className="text-xs text-muted-foreground">{skill?.experience}</div>
        </div>
      </div>
      {/* Proficiency Bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs text-muted-foreground">Proficiency</span>
          <span className="text-xs text-muted-foreground">{skill?.percentage}%</span>
        </div>
        <div className="w-full bg-muted/30 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-smooth ${
              isHovered ? 'bg-conversion-accent' : 'bg-trust-builder'
            } ${getProficiencyWidth(skill?.proficiency)}`}
          />
        </div>
      </div>
      {/* Recent Projects */}
      <div className="mb-4">
        <div className="text-xs text-muted-foreground mb-2">Recent Projects</div>
        <div className="flex flex-wrap gap-1">
          {skill?.recentProjects?.slice(0, 3)?.map((project, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-muted/20 text-xs text-muted-foreground rounded-md"
            >
              {project}
            </span>
          ))}
          {skill?.recentProjects?.length > 3 && (
            <span className="px-2 py-1 bg-muted/20 text-xs text-muted-foreground rounded-md">
              +{skill?.recentProjects?.length - 3}
            </span>
          )}
        </div>
      </div>
      {/* Hover Details */}
      <div className={`transition-smooth ${isHovered ? 'opacity-100 max-h-40' : 'opacity-0 max-h-0'} overflow-hidden`}>
        <div className="border-t border-border pt-4">
          <div className="text-xs text-muted-foreground mb-2">Related Technologies</div>
          <div className="flex flex-wrap gap-1">
            {skill?.relatedTech?.map((tech, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-conversion-accent/10 text-xs text-conversion-accent rounded-md"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </div>
      {/* Active Indicator */}
      {isActive && (
        <div className="absolute -inset-0.5 bg-gradient-to-r from-conversion-accent/20 to-trust-builder/20 rounded-xl blur opacity-75" />
      )}
    </div>
  );
};

export default SkillCard;